import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Phone, ArrowRight, ShieldCheck, RotateCcw, Lock } from "lucide-react";
import { authAPI } from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_venue-onboard/artifacts/der85h7h_WhatsApp%20Image%202026-04-24%20at%2011.33.28.jpeg";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("bmm_token");
    const user = JSON.parse(localStorage.getItem("bmm_user") || "{}");
    if (token && ["admin", "subadmin"].includes(user.role)) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      await authAPI.requestOTP(phone);
      setStep("otp");
      setCountdown(60);
      toast.success("OTP sent to your phone");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 4) return;
    setLoading(true);
    try {
      const res = await authAPI.verifyOTP(phone, otp);
      const user = res.data.user;
      if (!["admin", "subadmin"].includes(user.role)) {
        toast.error("Access denied. Admin credentials required.");
        setOtp("");
        setLoading(false);
        return;
      }
      localStorage.setItem("bmm_token", res.data.token);
      localStorage.setItem("bmm_user", JSON.stringify(user));
      toast.success("Admin login successful!");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      await authAPI.requestOTP(phone);
      setCountdown(60);
      setOtp("");
      toast.success("OTP resent");
    } catch (err) {
      toast.error("Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otp.length === 4 && step === "otp") {
      handleVerifyOTP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8FAFC]">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            <Lock className="h-4 w-4 text-purple-600" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Admin Portal</span>
          </div>
          <div className="flex justify-center mb-3">
            <img src={LOGO_URL} alt="BookMyMandap" className="h-16 object-contain" />
          </div>
          <p className="text-sm font-medium text-slate-500">Venue Onboarding Portal</p>
        </div>

        <Card className="border-slate-200 shadow-xl shadow-slate-200/50">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {step === "phone" ? "Admin Sign In" : "Verify OTP"}
            </CardTitle>
            <CardDescription className="text-sm text-slate-500">
              {step === "phone"
                ? "Enter your registered admin mobile number"
                : `OTP sent to +91 ${phone}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === "phone" ? (
              <form onSubmit={handleRequestOTP} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="admin-phone" className="text-sm font-medium text-slate-700">Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="admin-phone-input"
                      id="admin-phone"
                      type="tel"
                      placeholder="Admin mobile number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 h-12"
                      maxLength={10}
                    />
                  </div>
                </div>
                <Button
                  data-testid="admin-request-otp-btn"
                  type="submit"
                  disabled={loading || phone.length < 10}
                  className="w-full h-12 bg-purple-600 text-white hover:bg-purple-700 hover:-translate-y-0.5 transition-all duration-200 shadow-md shadow-purple-600/20 font-medium"
                >
                  {loading ? "Sending..." : "Get OTP"}
                  {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <InputOTP
                    data-testid="admin-otp-input"
                    maxLength={4}
                    value={otp}
                    onChange={setOtp}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                      <InputOTPSlot index={1} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                      <InputOTPSlot index={2} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                      <InputOTPSlot index={3} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {loading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                    <ShieldCheck className="h-4 w-4 animate-pulse" />
                    Verifying...
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <button
                    data-testid="admin-resend-otp-btn"
                    onClick={handleResend}
                    disabled={countdown > 0 || loading}
                    className="text-purple-600 hover:text-purple-700 font-medium disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Resend OTP
                  </button>
                  {countdown > 0 && (
                    <span className="text-slate-400">{countdown}s</span>
                  )}
                </div>

                <Button
                  data-testid="admin-change-number-btn"
                  variant="ghost"
                  onClick={() => { setStep("phone"); setOtp(""); }}
                  className="w-full text-slate-500 hover:text-slate-700"
                >
                  Change phone number
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          By continuing, you agree to our Terms of Service
        </p>
      </div>
    </div>
  );
}
